import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Plus, X } from "lucide-react";

import { ButtonLoader } from "components/Loader/ButtonLoader";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { ErrorComponent } from "components/Alerts/Error";
import { FormInput } from "components/ui/FormInput";
import { PageWrapper } from "components/ui/PageWrapper";

import {
  GetCustomSynthesisCroSection,
  UpdateCustomSynthesisCroSection,
  GetCustomSynthesisCroTags,
  AddCustomSynthesisCroTag,
  UpdateCustomSynthesisCroTag,
  DeleteCustomSynthesisCroTag,
  GetCustomSynthesisCroFaqs,
  AddCustomSynthesisCroFaq,
  UpdateCustomSynthesisCroFaq,
  DeleteCustomSynthesisCroFaq,
} from "rest/customSynthesisCro";

export default function HomeCustomSynthesisCro() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const getSection = GetCustomSynthesisCroSection();
  const updateSection = UpdateCustomSynthesisCroSection();

  useEffect(() => {
    const data = getSection?.data?.data;
    if (data) {
      reset({
        heading: data.heading || "",
        subheading: data.subheading || "",
        lead_1: data.lead_1 || "",
        lead_2: data.lead_2 || "",
        capabilities_heading: data.capabilities_heading || "",
        faq_heading: data.faq_heading || "",
        cta_text: data.cta_text || "",
        cta_button_text: data.cta_button_text || "",
        cta_button_link: data.cta_button_link || "",
      });
    }
  }, [getSection?.data?.data, reset]);

  const onSubmit = (data) => {
    updateSection.mutate(data);
  };

  return (
    <div className="custom-synthesis-cro-page">
      <PageWrapper slug="home-custom-synthesis-cro" name="Custom Synthesis CRO" />

      {getSection?.isError && (
        <ErrorComponent message="OOPS ! something went wrong please try again later" />
      )}

      {getSection?.isPending ? (
        <ComponentLoader />
      ) : (
        <div className="home_banner_input">
          <form onSubmit={handleSubmit(onSubmit)} className="row mt-4 mb-3">
            <div className="mb-3 col-md-6">
              <label htmlFor="heading" className="form-label">
                Heading
              </label>
              <FormInput
                type="text"
                name="heading"
                placeholder="Custom Synthesis CRO Services"
                {...register("heading", { required: true })}
              />
              {errors?.heading && (
                <p className="errorMessage">Heading is required</p>
              )}
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="subheading" className="form-label">
                Subheading
              </label>
              <FormInput
                type="text"
                name="subheading"
                placeholder="Challenging Chemistry, Managed with Clarity"
                {...register("subheading")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="lead_1" className="form-label">
                Lead Paragraph 1
              </label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="First lead paragraph"
                {...register("lead_1")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="lead_2" className="form-label">
                Lead Paragraph 2
              </label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Second lead paragraph"
                {...register("lead_2")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="capabilities_heading" className="form-label">
                Capabilities Heading
              </label>
              <FormInput
                type="text"
                name="capabilities_heading"
                placeholder="Custom Synthesis Capabilities"
                {...register("capabilities_heading")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="faq_heading" className="form-label">
                FAQ Heading
              </label>
              <FormInput
                type="text"
                name="faq_heading"
                placeholder="What Can We Help You Solve?"
                {...register("faq_heading")}
              />
            </div>

            <div className="mb-3 col-md-12">
              <label htmlFor="cta_text" className="form-label">
                CTA Text
              </label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Have a challenging synthesis project? Talk with our chemists..."
                {...register("cta_text")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="cta_button_text" className="form-label">
                CTA Button Text
              </label>
              <FormInput
                type="text"
                name="cta_button_text"
                placeholder="Contact Us"
                {...register("cta_button_text")}
              />
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="cta_button_link" className="form-label">
                CTA Button Link
              </label>
              <FormInput
                type="text"
                name="cta_button_link"
                placeholder="/contact-us"
                {...register("cta_button_link")}
              />
            </div>

            {updateSection?.isPending ? (
              <div className="col-12 text-center">
                <ButtonLoader />
              </div>
            ) : (
              <div className="mb-3 col-12">
                <input type="submit" value="Save Section" className="input_submit" />
              </div>
            )}
          </form>
        </div>
      )}

      <hr className="my-4" />
      <div className="row">
        <div className="col-md-4">
          <CapabilityTags />
        </div>
        <div className="col-md-8">
          <SectionFaqs />
        </div>
      </div>
    </div>
  );
}

const CapabilityTags = () => {
  const getTags = GetCustomSynthesisCroTags();
  const addTag = AddCustomSynthesisCroTag();
  const updateTag = UpdateCustomSynthesisCroTag();
  const deleteTag = DeleteCustomSynthesisCroTag();

  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  const tags = getTags?.data?.data || [];

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    addTag.mutate(
      { label: newLabel.trim() },
      { onSuccess: () => setNewLabel("") }
    );
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditingLabel(tag.label);
  };

  const saveEdit = (id) => {
    if (!editingLabel.trim()) return;
    updateTag.mutate(
      { id, data: { label: editingLabel.trim() } },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleDelete = (id) => {
    deleteTag.mutate(id);
  };

  return (
    <div className="custom-synthesis-cro-tags">
      <h4 className="fw-bold mb-3">Capability Tags</h4>

      {getTags?.isPending ? (
        <ComponentLoader />
      ) : (
        <>
          {tags.map((tag) => (
            <div key={tag.id} className="d-flex align-items-center gap-2 mb-2">
              {editingId === tag.id ? (
                <>
                  <input
                    type="text"
                    className="form-control"
                    value={editingLabel}
                    onChange={(e) => setEditingLabel(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => saveEdit(tag.id)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control"
                    value={tag.label}
                    readOnly
                    onClick={() => startEdit(tag)}
                    style={{ flex: 1, cursor: "pointer" }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(tag.id)}
                    style={{ minWidth: "40px" }}
                  >
                    <X size={16} />
                  </button>
                </>
              )}
            </div>
          ))}

          <div className="d-flex align-items-center gap-2 mt-3">
            <input
              type="text"
              className="form-control"
              placeholder="New capability tag"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center gap-2"
              onClick={handleAdd}
            >
              <Plus size={16} />
              Add Tag
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const SectionFaqs = () => {
  const getFaqs = GetCustomSynthesisCroFaqs();
  const addFaq = AddCustomSynthesisCroFaq();
  const updateFaq = UpdateCustomSynthesisCroFaq();
  const deleteFaq = DeleteCustomSynthesisCroFaq();

  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState("");
  const [editingAnswer, setEditingAnswer] = useState("");

  const faqs = getFaqs?.data?.data || [];

  const handleAdd = () => {
    if (!newQuestion.trim() || !newAnswer.trim()) return;
    addFaq.mutate(
      { question: newQuestion.trim(), answer: newAnswer.trim() },
      {
        onSuccess: () => {
          setNewQuestion("");
          setNewAnswer("");
        },
      }
    );
  };

  const startEdit = (faq) => {
    setEditingId(faq.id);
    setEditingQuestion(faq.question);
    setEditingAnswer(faq.answer);
  };

  const saveEdit = (id) => {
    if (!editingQuestion.trim() || !editingAnswer.trim()) return;
    updateFaq.mutate(
      { id, data: { question: editingQuestion.trim(), answer: editingAnswer.trim() } },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleDelete = (id) => {
    deleteFaq.mutate(id);
  };

  return (
    <div className="custom-synthesis-cro-faqs">
      <h4 className="fw-bold mb-3">FAQs</h4>

      {getFaqs?.isPending ? (
        <ComponentLoader />
      ) : (
        <>
          {faqs.map((faq) => (
            <div key={faq.id} className="card mb-2">
              <div className="card-body">
                {editingId === faq.id ? (
                  <>
                    <input
                      type="text"
                      className="form-control mb-2"
                      value={editingQuestion}
                      onChange={(e) => setEditingQuestion(e.target.value)}
                      placeholder="Question"
                    />
                    <textarea
                      className="form-control mb-2"
                      rows="3"
                      value={editingAnswer}
                      onChange={(e) => setEditingAnswer(e.target.value)}
                      placeholder="Answer"
                    />
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => saveEdit(faq.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div style={{ flex: 1, cursor: "pointer" }} onClick={() => startEdit(faq)}>
                        <p className="fw-bold mb-1">{faq.question}</p>
                        <p className="mb-0 text-muted">{faq.answer}</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(faq.id)}
                        style={{ minWidth: "40px" }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          <div className="card mt-3">
            <div className="card-body">
              <p className="fw-bold mb-2">Add New FAQ</p>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Question"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
              />
              <textarea
                className="form-control mb-2"
                rows="3"
                placeholder="Answer"
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                onClick={handleAdd}
              >
                <Plus size={16} />
                Add FAQ
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
